import * as THREE from 'three/webgpu';
import { PointsMaterialFactory } from '../../material/PointsMaterialFactory';
import type { SketchGroup } from '../../mesh/sketch';
import type {
	SketchEntityReferenceMoveDetachContext,
	SketchEntityReferenceMoveDetachPort,
} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceMoveDetachTypes';
import { SketchEntityReferenceMoveDetachService } from '../../reference/sketch-entity-reference/lifecycle/SketchEntityReferenceMoveDetachService';
import type { SketchRuntimeHistorySession, Viewer } from '../../viewer/Viewer';
import type { SketchCollectionChildMoveDetachContext } from './SketchCollectionChildMoveDetachService';
import { AbstractSketchEntitiesTransformController } from './AbstractSketchEntitiesTransformController';
import {
	SketchEntitiesTransformCommand,
	SketchEntitiesTransformInputMode,
	SketchEntitiesTransformPhase,
	SketchEntitiesTransformRelationRetentionMode,
	SketchEntitiesTransformSelectionRole,
	type SketchEntitiesTransformRepeatRecord,
} from './SketchEntitiesTransformTypes';
import {
	SketchRelationRetentionService,
	type SketchRelationRetentionContext,
} from './SketchRelationRetentionService';
import {
	clearSketchEntitiesTransformPointSnapFeedback,
	createSketchEntitiesTransformPointSnapExclusionIds,
	resolveSketchEntitiesTransformPointSnap,
} from './utils/SketchEntitiesTransformPointSnapResolver';
import {SketchPlanePointerIntersectionResolver} from './SketchPlanePointerIntersectionResolver';

/**
 * 移动实体 X/Y 重复轴。
 */
enum SketchMoveEntitiesXYRepeatAxis {
	/** 未记录重复轴。 */
	None = 'none',
	/** 重复 X 轴输入值。 */
	X = 'x',
	/** 重复 Y 轴输入值。 */
	Y = 'y',
}

/**
 * 草图移动实体控制器。
 */
export class SketchMoveEntitiesController extends AbstractSketchEntitiesTransformController {
	/** 起点黄色标记根对象。 */
	private fromPointMarkerRoot: THREE.Group | null = null;
	/** 鼠标驱动预览参考平面。 */
	private pointerDrivenPreviewPlane: THREE.Plane | null = null;
	/** 鼠标驱动预览当前目标点。 */
	private pointerDrivenPreviewPoint: THREE.Vector3 | null = null;
	/** X/Y 模式下 X 方向重复步长。 */
	private xyRepeatStepX = 0;
	/** X/Y 模式下 Y 方向重复步长。 */
	private xyRepeatStepY = 0;
	/** X/Y 模式下当前允许重复的单一轴。 */
	private xyRepeatAxis = SketchMoveEntitiesXYRepeatAxis.None;
	/** 几何关系保留服务。 */
	private readonly relationRetentionService = new SketchRelationRetentionService();
	/** 预览阶段临时断开的外部关系上下文。 */
	private relationRetentionPreviewContext: SketchRelationRetentionContext | null = null;
	/** 预览阶段临时解除的转换实体引用上下文。 */
	private referencedEntityMovePreviewContext: SketchEntityReferenceMoveDetachContext | null = null;
	/** 移动预览基线点位快照。 */
	private movePreviewBaseline: Map<string, THREE.Vector3> | null = null;
	/** 当前是否已经把移动预览应用到草图实体。 */
	private movePreviewApplied = false;
	/** 移动确认阶段使用的运行时历史会话。 */
	private moveRuntimeHistorySession: SketchRuntimeHistorySession | null = null;
	/** 集合子边移动拆分上下文。 */
	private collectionChildMoveDetachContext: SketchCollectionChildMoveDetachContext | null = null;
	/** 鼠标到草图平面的自由求交解析器。 */
	private readonly planePointerIntersectionResolver = new SketchPlanePointerIntersectionResolver();

	/**
	 * @param viewer Viewer 实例。
	 * @param referencedEntityMoveDetachPort 转换实体移动解除引用端口。
	 */
	public constructor(
		viewer: Viewer,
		private readonly referencedEntityMoveDetachPort: SketchEntityReferenceMoveDetachPort = (
			new SketchEntityReferenceMoveDetachService()
		),
	) {
		super(viewer, );
	}

	/**
	 * 启动移动实体命令。
	 *
	 * 设计说明：
	 * 1. 每次进入命令都必须重置 X/Y Repeat 状态，避免沿用上一次会话的步长；
	 * 2. 启动后立即同步起点标记层，保证 From/To 模式切回时界面状态一致；
	 * 3. 具体的实体选择与预览基线仍由父类和后续交互事件驱动。
	 */
	public override start(initialEntities: readonly THREE.Object3D[] = []): void {
		super.start(initialEntities);
		this.resetXYRepeatStep();
		this.syncFromPointMarker();
	}

	/**
	 * 清理移动实体命令状态。
	 *
	 * 设计说明：
	 * 1. 退出命令前必须先清空鼠标驱动预览态与起点标记；
	 * 2. Repeat 状态属于本次命令会话，不允许泄漏到下次启动；
	 * 3. 其余通用状态由父类统一回收。
	 */
	public override clear(): void {
		this.resetPointerDrivenPreview();
		this.clearFromPointMarker();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		this.resetXYRepeatStep();
		super.clear();
	}

	/**
	 * 释放移动实体控制器资源。
	 *
	 * 设计说明：
	 * 1. 控制器销毁时要确保预览态对象全部解绑；
	 * 2. 起点标记属于场景临时对象，必须在销毁前移除；
	 * 3. 父类负责完成快照流等通用资源释放。
	 */
	public override dispose(): void {
		this.resetPointerDrivenPreview();
		this.clearFromPointMarker();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		super.dispose();
	}

	/**
	 * 设置当前输入模式。
	 *
	 * @param mode 目标输入模式。
	 *
	 * 设计说明：
	 * 1. 离开 From/To 模式时要立刻关闭鼠标驱动预览，避免旧平面继续生效；
	 * 2. 进入 X/Y 模式时统一补齐默认值 0，使面板显示、确认态与预览计算保持一致；
	 * 3. 模式切换后立即同步起点标记，防止残留的可视化状态误导用户。
	 */
	public override setInputMode(mode: SketchEntitiesTransformInputMode): void {
		if (mode !== SketchEntitiesTransformInputMode.FromTo) {
			this.resetPointerDrivenPreview();
			clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		}
		// X/Y 模式进入时使用 SolidWorks 面板风格的明确数值默认值，避免空值导致确认态与输入框显示不一致。
		if (mode === SketchEntitiesTransformInputMode.Coordinate) {
			this.targetX ??= 0;
			this.targetY ??= 0;
			this.xyRepeatStepX = this.targetX ?? 0;
			this.xyRepeatStepY = this.targetY ?? 0;
			this.xyRepeatAxis = SketchMoveEntitiesXYRepeatAxis.None;
		}
		super.setInputMode(mode);
		this.syncFromPointMarker();
	}

	//------ 移动实体保留几何关系 开始------
	/**
	 * 设置移动实体几何关系保留模式。
	 *
	 * @param mode 几何关系保留模式。
	 *
	 * 设计说明：
	 * 1. 关系保留模式会改变预览阶段是否受外部约束影响，因此切换时必须先回滚旧预览；
	 * 2. 回滚后再尝试按新模式重新预览，保证视口状态与 PropertyManager 选项一致；
	 * 3. Move 直接保存模式并派发快照，避免父类提前发出尚未重建预览的中间状态。
	 */
	public override setRelationRetentionMode(mode: SketchEntitiesTransformRelationRetentionMode): void {
		this.cacheRelationRetentionMode(mode);
		if (this.relationRetentionMode === mode) {
			return;
		}
		this.rollbackPreview();
		this.relationRetentionMode = mode;
		this.tryPreview();
		this.notifyStateChanged();
	}
	//------ 移动实体保留几何关系 结束------

	//------ 移动实体XY重复数值 开始------
	/**
	 * 设置 X/Y 模式下的目标坐标输入。
	 *
	 * @param axis 当前编辑轴。
	 * @param value 当前输入值。
	 *
	 * 设计说明：
	 * 1. Move 的 Repeat 只重复最后一次编辑的轴，因此这里要同步记录步长与轴向；
	 * 2. 空值按 0 收口，避免 Repeat 步长进入未定义状态；
	 * 3. 真正的状态刷新和预览重算仍交给父类统一处理。
	 */
	public override setTargetCoordinate(axis: 'x' | 'y', value: number | null): void {
		if (axis === 'x') {
			this.xyRepeatStepX = value ?? 0;
			this.xyRepeatAxis = SketchMoveEntitiesXYRepeatAxis.X;
		} else {
			this.xyRepeatStepY = value ?? 0;
			this.xyRepeatAxis = SketchMoveEntitiesXYRepeatAxis.Y;
		}
		super.setTargetCoordinate(axis, value);
	}

	/**
	 * 重置 X/Y 重复步长。
	 */
	private resetXYRepeatStep(): void {
		this.xyRepeatStepX = 0;
		this.xyRepeatStepY = 0;
		this.xyRepeatAxis = SketchMoveEntitiesXYRepeatAxis.None;
	}
	//------ 移动实体XY重复数值 结束------

	/**
	 * 处理参考点选择。
	 *
	 * @param candidate 当前命中的参考点候选。
	 * @returns 当前命中是否被移动实体命令消费。
	 *
	 * 设计说明：
	 * 1. From/To 模式在选择起点前，需要先清空旧的鼠标驱动预览上下文；
	 * 2. 起点选定后立即进入“鼠标驱动落点”语义，不再等待单独选择 To 点；
	 * 3. 成功选点后要同步刷新阶段和标记，保证 PropertyManager 与视口状态一致。
	 */
	public override handleReferencePointSelection(candidate: Parameters<AbstractSketchEntitiesTransformController['handleReferencePointSelection']>[0]): boolean {
		const shouldStartPointerDrivenPreview = this.inputMode === SketchEntitiesTransformInputMode.FromTo
			&& this.activeSelectionRole === SketchEntitiesTransformSelectionRole.FromPoint;
		if (shouldStartPointerDrivenPreview) {
			this.resetPointerDrivenPreview();
		}
		const handled = super.handleReferencePointSelection(candidate);
		if (!handled) {
			return false;
		}
		if (shouldStartPointerDrivenPreview && this.fromPoint) {
			this.initializePointerDrivenPreview(this.fromPoint.position);
			this.activeSelectionRole = SketchEntitiesTransformSelectionRole.None;
			this.refreshPhase();
			this.notifyStateChanged();
		}
		this.syncFromPointMarker();
		return true;
	}

	/**
	 * 判断当前是否处于鼠标驱动预览态。
	 *
	 * @returns 是否处于鼠标驱动预览态。
	 */
	public isPointerDrivenPreviewActive(): boolean {
		return this.inputMode === SketchEntitiesTransformInputMode.FromTo
			&& !!this.fromPoint
			&& !!this.pointerDrivenPreviewPlane;
	}

	/**
	 * 根据视口鼠标位置刷新移动预览。
	 *
	 * 说明：
	 * 1. 起点选定后，Move 命令进入“抓手点拖动”语义；
	 * 2. 当前鼠标与草图基准面的交点，表示抓手点这一帧应到达的位置；
	 * 3. 控制器每次都基于原始起点与当前交点重算绝对位移，不做逐帧累计。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 * @returns 是否已消费本次鼠标移动。
	 */
	public handlePointerDrivenPreviewMove(clientX: number, clientY: number): boolean {
		if (!this.isPointerDrivenPreviewActive()) {
			return false;
		}

		const nextPoint = this.resolvePointerDrivenPreviewPoint(clientX, clientY);
		if (!nextPoint) {
			return false;
		}

		this.pointerDrivenPreviewPoint = nextPoint;
		this.tryPreview();
		this.syncFromPointMarker();
		this.notifyStateChanged();
		return true;
	}

	/**
	 * 处理鼠标驱动预览态下的落点点击。
	 *
	 * @returns 是否已消费本次点击。
	 */
	public handlePointerDrivenPreviewPlacement(): boolean {
		if (!this.isPointerDrivenPreviewActive()) {
			return false;
		}
		return this.confirm();
	}

	//------ 移动实体XY重复数值 开始------
	/**
	 * 执行 Repeat 操作。
	 *
	 * @returns Repeat 是否成功执行。
	 *
	 * 设计说明：
	 * 1. X/Y 模式下的 Repeat 不是直接复用历史矩阵，而是修改当前面板输入值并刷新预览；
	 * 2. 一次只递增最后编辑的轴，保持与 SolidWorks 面板连续输入语义一致；
	 * 3. 非 X/Y 模式则回退到父类的通用 Repeat 记录回放链路。
	 */
	public override repeat(): boolean {
		if (this.inputMode !== SketchEntitiesTransformInputMode.Coordinate) {
			return super.repeat();
		}
		if (!this.canRepeat()) {
			return false;
		}

		// Repeat 在 X/Y 模式下一次只重复最后编辑的轴，避免设置 Y 后同时改变 X。
		if (this.xyRepeatAxis === SketchMoveEntitiesXYRepeatAxis.X) {
			this.targetX = (this.targetX ?? 0) + this.xyRepeatStepX;
		}
		if (this.xyRepeatAxis === SketchMoveEntitiesXYRepeatAxis.Y) {
			this.targetY = (this.targetY ?? 0) + this.xyRepeatStepY;
		}
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
		return true;
	}
	//------ 移动实体XY重复数值 结束------

	/**
	 * 解析当前控制器对应的命令类型。
	 *
	 * @returns 移动实体命令枚举值。
	 */
	protected resolveCommandType(): SketchEntitiesTransformCommand {
		return SketchEntitiesTransformCommand.Move;
	}

	/**
	 * 解析当前输入对应的最终变换矩阵。
	 *
	 * @returns 当前位移对应的平移矩阵。
	 */
	protected resolveTransformMatrix(): THREE.Matrix4 {
		return this.geometryService.createTranslationMatrix(this.resolveTranslationVector());
	}

	/**
	 * 将已解析的变换应用到当前选中实体。
	 *
	 * @param matrix 待应用的变换矩阵。
	 */
	protected applyResolvedTransform(matrix: THREE.Matrix4): void {
		if (!this.applyParametricTransform(
			this.selectedEntities,
			matrix,
			'move-entities',
			this.movePreviewBaseline ?? undefined,
		)) {
			throw new Error('移动实体失败：统一参数系统拒绝目标位置。');
		}
	}

	//------ 移动实体确认式历史会话 开始------
	/**
	 * 根据当前命令状态刷新阶段。
	 *
	 * 设计说明：
	 * 1. Move 的 Previewing 阶段依赖 `movePreviewApplied`，而不是父类通用预览对象是否存在；
	 * 2. 只要参数完整且预览尚未真正写入实体，就保持 Ready；
	 * 3. 其它情况下按当前选择角色回退到选实体或选参考点阶段。
	 */
	protected override refreshPhase(): void {
		if (!this.active) {
			this.phase = SketchEntitiesTransformPhase.Cancelled;
			return;
		}
		if (this.canConfirm()) {
			this.phase = this.movePreviewApplied
				? SketchEntitiesTransformPhase.Previewing
				: SketchEntitiesTransformPhase.Ready;
			return;
		}
		this.phase = this.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
			? SketchEntitiesTransformPhase.SelectEntities
			: SketchEntitiesTransformPhase.SelectReference;
	}

	/**
	 * 尝试应用当前移动预览。
	 *
	 * 设计说明：
	 * 1. Move 预览必须先恢复到命令基线，再应用本帧绝对位移，避免逐帧累计误差；
	 * 2. 当完整选择闭合集合组成边时，这里还要提前完成拆分或 collection 级收口；
	 * 3. 零位移预览直接回滚，避免创建无意义的预览态与阶段切换。
	 */
	protected override tryPreview(): void {
		if (!this.active) {
			return;
		}
		if (!this.canConfirm()) {
			this.rollbackPreview();
			return;
		}
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return;
		}

		const translation = this.resolveTranslationVector();
		if (translation.lengthSq() <= 1e-20) {
			this.rollbackPreview();
			return;
		}

		this.ensureReferencedEntityMovePreviewContext();
		this.ensureCollectionChildMoveDetachContext(activeSketchGroup);
		this.ensureMovePreviewBaseline();
		this.ensureRelationRetentionPreviewContext(activeSketchGroup);
		this.applyResolvedTransform(this.geometryService.createTranslationMatrix(translation));
		this.movePreviewApplied = true;
		this.viewer.requestRender();
		this.refreshPhase();
	}

	/**
	 * 回滚当前移动预览。
	 *
	 * 设计说明：
	 * 1. 若本轮预览已写入实体，必须先按基线恢复原始状态；
	 * 2. 子边拆分只属于当前命令会话的临时拓扑，取消预览时需要一并回滚；
	 * 3. 只有真的发生过恢复或拓扑回滚时，才请求重新渲染。
	 */
	protected override rollbackPreview(): void {
		let shouldRequestRender = false;
		if (this.movePreviewApplied) {
			this.restoreMovePreviewBaseline();
			shouldRequestRender = true;
		}
		if (this.relationRetentionPreviewContext) {
			this.rollbackRelationRetentionPreviewContext();
			shouldRequestRender = true;
		}
		if (this.referencedEntityMovePreviewContext) {
			this.rollbackReferencedEntityMovePreviewContext();
			shouldRequestRender = true;
		}
		this.movePreviewBaseline = null;
		this.movePreviewApplied = false;
		if (this.collectionChildMoveDetachContext) {
			this.collectionChildMoveDetachContext.rollback();
			this.selectedEntities = [...this.collectionChildMoveDetachContext.originalSelectedEntities];
			this.collectionChildMoveDetachContext = null;
			this.syncSelectionVisuals(this.selectedEntities);
			shouldRequestRender = true;
		}
		if (shouldRequestRender) {
			this.viewer.requestRender();
		}
		this.refreshPhase();
	}

	/**
	 * 确认当前移动结果。
	 *
	 * @returns 确认是否成功。
	 *
	 * 设计说明：
	 * 1. Move 与 Copy 不同，确认前要先把预览过的原对象恢复到基线，再以最终矩阵正式提交；
	 * 2. 运行时历史会话只在确认阶段创建，保证预览不进入 Undo/Redo；
	 * 3. 若确认失败，必须同时回滚集合子边拆分上下文与历史会话，避免留下半提交状态。
	 * @throws 当关系保留、几何变换、草图提交或历史提交失败时，回滚确认上下文并原样重新抛出原异常。
	 */
	public override confirm(): boolean {
		if (!this.canConfirm()) {
			return false;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return false;
		}

		const matrix = this.resolveTransformMatrix();
		// 非零移动才需要在确认事务内重新拆分集合子边，避免零位移确认破坏原集合拓扑。
		const shouldDetachCollectionChildForConfirm = this.resolveTranslationVector().lengthSq() > 1e-20;
		if (this.movePreviewApplied) {
			this.restoreMovePreviewBaseline();
			this.movePreviewApplied = false;
		}
		if (this.relationRetentionPreviewContext) {
			this.rollbackRelationRetentionPreviewContext();
		}
		if (this.referencedEntityMovePreviewContext) {
			this.rollbackReferencedEntityMovePreviewContext();
		}
		if (this.collectionChildMoveDetachContext) {
			// 确认前必须回到原始集合拓扑，让运行时历史 before 捕获矩形共享控制点。
			this.rollbackCollectionChildMoveDetachContext();
			// 原预览基线绑定的是已拆分对象，回到原拓扑后必须丢弃。
			this.movePreviewBaseline = null;
		}

		this.moveRuntimeHistorySession = this.viewer.beginActiveSketchRuntimeHistorySession({
			label: this.resolveHistoryLabel(),
			commandType: this.resolveHistoryCommandType(),
		});
		let confirmRelationRetentionContext: SketchRelationRetentionContext | null = null;
		let confirmReferencedEntityMoveContext: SketchEntityReferenceMoveDetachContext | null = null;
		try {
			if (shouldDetachCollectionChildForConfirm) {
				confirmReferencedEntityMoveContext = this.detachReferencedEntitiesForMove();
				// 集合子边拆分必须发生在历史会话内，才能让 Undo 同时覆盖拆分和最终位移。
				this.ensureCollectionChildMoveDetachContext(activeSketchGroup);
			}
			confirmRelationRetentionContext = this.applyRelationRetentionForConfirm(activeSketchGroup);
			this.applyResolvedTransform(matrix);
			this.commitSketchGroup(activeSketchGroup);
			this.lastRepeatRecord = this.createRepeatRecord(matrix);
			this.movePreviewBaseline = null;
			this.phase = SketchEntitiesTransformPhase.Committed;
			this.commitCollectionChildMoveDetachContext();
			confirmRelationRetentionContext?.commit();
			this.commitMoveRuntimeHistorySession(
				confirmReferencedEntityMoveContext?.historyPatches ?? [],
				() => confirmReferencedEntityMoveContext?.commit(),
				() => confirmReferencedEntityMoveContext?.rollback(),
			);
		} catch (error) {
			confirmReferencedEntityMoveContext?.rollback();
			confirmRelationRetentionContext?.rollback();
			this.rollbackCollectionChildMoveDetachContext();
			this.cancelMoveRuntimeHistorySession();
			throw error;
		}
		this.notifyStateChanged();
		this.viewer.clearSketchMoveEntitiesMode();
		return true;
	}

	/**
	 * 取消当前移动命令。
	 *
	 * 设计说明：
	 * 1. 取消时要同时清空鼠标驱动预览和平移预览；
	 * 2. 阶段显式切回 Cancelled，避免外部状态还认为命令可继续确认；
	 * 3. 起点标记也要同步收口，避免视口残留黄色点。
	 */
	public override cancel(): void {
		this.resetPointerDrivenPreview();
		this.rollbackPreview();
		this.phase = SketchEntitiesTransformPhase.Cancelled;
		this.syncFromPointMarker();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		this.notifyStateChanged();
	}

	/**
	 * 回放一条已提交的 Repeat 记录。
	 *
	 * @param record 上一次提交保存的重复记录。
	 * @param sketchGroup 当前活动草图组。
	 * @returns 回放是否成功。
	 */
	protected override applyRepeatRecord(record: SketchEntitiesTransformRepeatRecord, sketchGroup: SketchGroup): boolean {
		const entities = this.geometryService.resolveEntitiesByIds(sketchGroup, record.sourceEntityIds);
		if (entities.length === 0) {
			return false;
		}

		if (!this.applyParametricTransform(entities, record.matrix, 'move-entities-repeat')) {
			return false;
		}
		sketchGroup.markDefinitionDirty({
			reason: this.resolveHistoryCommandType(),
			sourceObjectId: entities[0]?.uuid,
		});
		sketchGroup.commitDefinitionChanges({
			reason: this.resolveHistoryCommandType(),
			sourceObjectId: entities[0]?.uuid,
		});
		this.viewer.requestRender();
		return true;
	}
	//------ 移动实体确认式历史会话 结束------

	/**
	 * 解析移动实体历史命令类型。
	 *
	 * @returns 运行时历史命令标识。
	 */
	protected resolveHistoryCommandType(): string {
		return 'sketch.runtime.move-entities';
	}

	/**
	 * 解析移动实体历史标签。
	 *
	 * @returns 历史记录展示标签。
	 */
	protected resolveHistoryLabel(): string {
		return '移动草图实体';
	}

	/**
	 * 判断当前是否允许确认。
	 *
	 * @returns 当前命令是否满足确认条件。
	 */
	protected canConfirm(): boolean {
		if (this.selectedEntities.length === 0) {
			return false;
		}
		if (this.inputMode === SketchEntitiesTransformInputMode.FromTo) {
			return !!this.fromPoint;
		}
		return this.targetX !== null || this.targetY !== null;
	}

	/**
	 * 判断当前是否允许 Repeat。
	 *
	 * @returns Repeat 是否可用。
	 */
	protected canRepeat(): boolean {
		if (this.inputMode === SketchEntitiesTransformInputMode.Coordinate) {
			return this.selectedEntities.length > 0
				&& this.resolveActiveSketchGroup() !== null
				&& this.xyRepeatAxis !== SketchMoveEntitiesXYRepeatAxis.None
				&& this.resolveXYRepeatStepTranslationVector().lengthSq() > 1e-20;
		}
		return this.lastRepeatRecord !== null;
	}

	/**
	 * 解析位移向量。
	 *
	 * @returns 位移向量。
	 */
	private resolveTranslationVector(): THREE.Vector3 {
		if (this.inputMode === SketchEntitiesTransformInputMode.FromTo && this.fromPoint) {
			const targetPoint = this.pointerDrivenPreviewPoint?.clone()
				?? this.fromPoint.position.clone();
			return targetPoint.sub(this.fromPoint.position);
		}
		//------ 移动实体XY平移距离 开始------
		const translation = this.resolveXYStepTranslationVector();
		//------ 移动实体XY平移距离 结束------
		return translation;
	}

	//------ 移动实体XY重复数值 开始------
	/**
	 * 解析 X/Y 当前输入对应的单次平移向量。
	 *
	 * @returns 单次平移向量。
	 */
	private resolveXYStepTranslationVector(): THREE.Vector3 {
		// X/Y 模式表示沿草图局部坐标轴的平移距离，不表示目标坐标。
		const deltaX = this.targetX ?? 0;
		const deltaY = this.targetY ?? 0;
		return this.createXYTranslationVector(deltaX, deltaY);
	}

	/**
	 * 解析 X/Y Repeat 使用的固定步长位移向量。
	 *
	 * @returns 固定步长位移向量。
	 */
	private resolveXYRepeatStepTranslationVector(): THREE.Vector3 {
		if (this.xyRepeatAxis === SketchMoveEntitiesXYRepeatAxis.X) {
			return this.createXYTranslationVector(this.xyRepeatStepX, 0);
		}
		if (this.xyRepeatAxis === SketchMoveEntitiesXYRepeatAxis.Y) {
			return this.createXYTranslationVector(0, this.xyRepeatStepY);
		}
		return new THREE.Vector3();
	}

	/**
	 * 根据 X/Y 数值创建草图局部坐标系中的世界平移向量。
	 *
	 * @param deltaX X 方向平移距离。
	 * @param deltaY Y 方向平移距离。
	 * @returns 世界平移向量。
	 */
	private createXYTranslationVector(deltaX: number, deltaY: number): THREE.Vector3 {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		const translation = activeSketchGroup
			? activeSketchGroup.referencePlane.xAxis.clone()
				.multiplyScalar(deltaX)
				.add(activeSketchGroup.referencePlane.yAxis.clone().multiplyScalar(deltaY))
			: new THREE.Vector3(deltaX, deltaY, 0);
		return translation;
	}
	//------ 移动实体XY重复数值 结束------

	//------ 移动实体保留几何关系 开始------
	/**
	 * 确保预览阶段已经按当前选项临时处理外部几何关系。
	 *
	 * @param sketchGroup 当前活动草图组。
	 *
	 * 设计说明：
	 * 1. 取消保留几何关系时，预览也必须摆脱外部关系约束，否则实体会被未选对象拉回；
	 * 2. 预览阶段的关系移除必须可回滚，不能提前进入历史事务；
	 * 3. 已存在预览上下文时直接复用，避免 mousemove 每帧重复删除关系。
	 */
	private ensureRelationRetentionPreviewContext(sketchGroup: SketchGroup): void {
		if (!this.shouldBreakExternalRelations() || this.relationRetentionPreviewContext) {
			return;
		}

		this.relationRetentionPreviewContext = this.relationRetentionService.detachExternalRelations({
			sketchGroup,
			selectedEntities: this.selectedEntities,
		});
	}

	/**
	 * 确保预览阶段已经解除所选转换实体的外部引用。
	 *
	 * 设计说明：
	 * 1. 转换实体的引用驱动约束属于所选草图实体与外部模型几何之间的关系；
	 * 2. 默认不保留关系时必须在几何变换前解除，否则求解器会把直线或圆拉回投影位置；
	 * 3. 预览取消或参数变化时通过同一个上下文恢复引用记录、标记和约束。
	 */
	private ensureReferencedEntityMovePreviewContext(): void {
		if (!this.shouldBreakExternalRelations() || this.referencedEntityMovePreviewContext) {
			return;
		}

		this.referencedEntityMovePreviewContext = this.referencedEntityMoveDetachPort.detach({
			selectedEntities: this.selectedEntities,
		});
	}

	/**
	 * 回滚预览阶段临时解除的转换实体引用。
	 */
	private rollbackReferencedEntityMovePreviewContext(): void {
		this.referencedEntityMovePreviewContext?.rollback();
		this.referencedEntityMovePreviewContext = null;
	}

	/**
	 * 在确认阶段正式解除所选转换实体的外部引用。
	 *
	 * @returns 可随历史提交结果确认或回滚的解除上下文。
	 */
	private detachReferencedEntitiesForMove(): SketchEntityReferenceMoveDetachContext | null {
		if (!this.shouldBreakExternalRelations()) {
			return null;
		}

		return this.referencedEntityMoveDetachPort.detach({
			selectedEntities: this.selectedEntities,
		});
	}

	/**
	 * 回滚预览阶段临时断开的外部几何关系。
	 */
	private rollbackRelationRetentionPreviewContext(): void {
		this.relationRetentionPreviewContext?.rollback();
		this.relationRetentionPreviewContext = null;
	}

	/**
	 * 在确认阶段正式应用几何关系保留策略。
	 *
	 * @param sketchGroup 当前活动草图组。
	 * @returns 可在确认失败时回滚的关系上下文。
	 */
	private applyRelationRetentionForConfirm(sketchGroup: SketchGroup): SketchRelationRetentionContext | null {
		if (!this.shouldBreakExternalRelations()) {
			return null;
		}

		return this.relationRetentionService.detachExternalRelations({
			sketchGroup,
			selectedEntities: this.selectedEntities,
		});
	}

	/**
	 * 判断当前是否需要断开外部几何关系。
	 *
	 * @returns 需要断开外部关系时返回 true。
	 */
	private shouldBreakExternalRelations(): boolean {
		return this.relationRetentionMode === SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations;
	}
	//------ 移动实体保留几何关系 结束------

	/**
	 * 同步起点黄色标记。
	 *
	 * 说明：
	 * 1. Move 命令在当前交互下把 From 点视为单点目标；
	 * 2. 选点后需要立即给出稳定的黄色标记，帮助用户理解当前目标点；
	 * 3. 标记对象本身不参与草图定义，仅作为预览层对象挂到 Viewer 场景。
	 */
	private syncFromPointMarker(): void {
		if (this.inputMode !== SketchEntitiesTransformInputMode.FromTo || !this.fromPoint) {
			this.clearFromPointMarker();
			return;
		}

		const markerRoot = this.ensureFromPointMarkerRoot();
		// 黄色标记表达用户选择的 From 起点，移动预览期间必须固定在原始起点位置。
		markerRoot.position.copy(this.fromPoint.position);
		this.viewer.syncSketchEditingVisualLayering({
			previewRoots: [markerRoot],
		});
		this.viewer.requestRender();
	}

	/**
	 * 初始化鼠标驱动预览态。
	 *
	 * @param basePoint 已选起点世界坐标。
	 */
	private initializePointerDrivenPreview(basePoint: THREE.Vector3): void {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			this.resetPointerDrivenPreview();
			return;
		}

		this.pointerDrivenPreviewPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
			activeSketchGroup.referencePlane.normal.clone(),
			basePoint.clone(),
		);
		this.pointerDrivenPreviewPoint = basePoint.clone();
	}

	/**
	 * 重置鼠标驱动预览态。
	 */
	private resetPointerDrivenPreview(): void {
		this.pointerDrivenPreviewPlane = null;
		this.pointerDrivenPreviewPoint = null;
	}

	//------ 移动实体闭合集合子边拆分 开始------
	/**
	 * 确保当前移动预览已拆分闭合集合子边。
	 *
	 * 设计说明：
	 * 1. 只有真正开始非零位移预览时，才需要把闭合集合子边拆出来；
	 * 2. 拆分结果只在 Move 命令当前会话内生效，取消时必须整体回滚；
	 * 3. 拆分后选中实体会替换为克隆边对象，以便与原集合断开共享控制点。
	 *
	 * @param sketchGroup 当前活动草图组。
	 */
	private ensureCollectionChildMoveDetachContext(sketchGroup: SketchGroup): void {
		if (this.collectionChildMoveDetachContext) {
			return;
		}

		const detachContext = this.geometryService.detachCollectionChildEntitiesForMove(this.selectedEntities, sketchGroup);
		if (!detachContext) {
			return;
		}

		this.collectionChildMoveDetachContext = detachContext;
		this.selectedEntities = [...detachContext.detachedSelectedEntities];
		this.syncSelectionVisuals(this.selectedEntities);
	}

	/**
	 * 提交集合子边移动拆分上下文。
	 */
	private commitCollectionChildMoveDetachContext(): void {
		this.collectionChildMoveDetachContext?.commit();
		this.collectionChildMoveDetachContext = null;
	}

	/**
	 * 回滚集合子边移动拆分上下文。
	 */
	private rollbackCollectionChildMoveDetachContext(): void {
		if (!this.collectionChildMoveDetachContext) {
			return;
		}

		this.collectionChildMoveDetachContext.rollback();
		this.selectedEntities = [...this.collectionChildMoveDetachContext.originalSelectedEntities];
		this.collectionChildMoveDetachContext = null;
		this.syncSelectionVisuals(this.selectedEntities);
	}
	//------ 移动实体闭合集合子边拆分 结束------

	//------ 移动实体确认式历史会话 开始------
	/**
	 * 确保移动预览基线已建立。
	 *
	 * 设计说明：
	 * 1. 移动实体预览阶段不能每帧恢复运行时快照，否则会干扰当前 ViewerMode；
	 * 2. 实时预览必须以命令开始预览前的点位为基准，避免 mousemove 逐帧累计误差；
	 * 3. 基线只保存唯一草图点的世界坐标，Undo/Redo 只在确认时创建运行时历史会话。
	 */
	private ensureMovePreviewBaseline(): void {
		if (this.movePreviewBaseline) {
			return;
		}

		this.movePreviewBaseline = new Map();
		this.geometryService.collectParametricRigidTransformPoints(this.selectedEntities).forEach((point) => {
			// 保存 clone，避免后续 setPosition 修改同一个 Vector3 引用。
			this.movePreviewBaseline?.set(point.uuid, point.getPosition().clone());
		});
	}

	/**
	 * 提交移动实体运行时历史会话。
	 */
	private commitMoveRuntimeHistorySession(
		extraPatches: SketchEntityReferenceMoveDetachContext['historyPatches'] = [],
		onCommitted: () => void = () => {},
		onFailed: () => void = () => {},
	): void {
		const historySession = this.moveRuntimeHistorySession;
		this.moveRuntimeHistorySession = null;

		if (!historySession) {
			onCommitted();
			return;
		}

		const commitResult = historySession.commit(
			extraPatches.length > 0 ? { extraPatches } : undefined,
		);
		if (commitResult && typeof (commitResult as PromiseLike<void>).then === 'function') {
			void (commitResult as PromiseLike<void>).then(onCommitted, (error) => {
				onFailed();
				(globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(error);
			});
			return;
		}
		onCommitted();
	}

	/**
	 * 取消移动实体运行时历史会话。
	 */
	private cancelMoveRuntimeHistorySession(): void {
		const historySession = this.moveRuntimeHistorySession;
		this.moveRuntimeHistorySession = null;
		historySession?.cancel();
	}

	/**
	 * 从移动预览基线恢复草图点位。
	 *
	 * 设计说明：
	 * 1. 每次预览前先恢复基线，再应用当前绝对位移；
	 * 2. 取消命令时同样复用该逻辑，把实体恢复到命令预览前；
	 * 3. 这里不创建或取消运行时历史会话，避免 X/Y 输入或 mousemove 预览进入 Undo。
	 */
	private restoreMovePreviewBaseline(): void {
		if (!this.movePreviewBaseline) {
			return;
		}

		if (!this.restoreParametricBaseline(
			this.selectedEntities,
			this.movePreviewBaseline,
			'move-entities-rollback',
		)) {
			throw new Error('移动实体回滚失败：统一参数系统拒绝原始点位。');
		}
	}
	//------ 移动实体确认式历史会话 结束------

	/**
	 * 解析鼠标驱动预览当前指向点。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 * @returns 捕捉后或自由平面上的目标点；无法求交时返回 null。
	 */
	private resolvePointerDrivenPreviewPoint(clientX: number, clientY: number): THREE.Vector3 | null {
		if (!this.pointerDrivenPreviewPlane) {
			return null;
		}

		const freePoint = this.planePointerIntersectionResolver.resolve(
			this.viewer,
			this.pointerDrivenPreviewPlane,
			clientX,
			clientY,
		);
		if (!freePoint) {
			return null;
		}

		const snapResolution = resolveSketchEntitiesTransformPointSnap({
			viewer: this.viewer,
			clientX,
			clientY,
			worldPoint: freePoint,
			excludedTargetObjectIds: createSketchEntitiesTransformPointSnapExclusionIds({
				entities: this.selectedEntities,
				geometryService: this.geometryService,
			}),
		});
		return snapResolution?.position.clone() ?? freePoint;
	}

	/**
	 * 确保起点黄色标记根对象存在。
	 *
	 * @returns 起点标记根对象。
	 */
	private ensureFromPointMarkerRoot(): THREE.Group {
		if (this.fromPointMarkerRoot) {
			return this.fromPointMarkerRoot;
		}

		const markerSprite = new THREE.Sprite(PointsMaterialFactory.getReferenceSourceHighlightMaterial());
		markerSprite.name = '移动实体-起点黄色标记';
		markerSprite.scale.set(0.009, 0.009, 0.009);

		const markerRoot = new THREE.Group();
		markerRoot.name = '移动实体-起点黄色标记根对象';
		markerRoot.add(markerSprite);
		this.viewer.addObject(markerRoot);
		this.fromPointMarkerRoot = markerRoot;
		return markerRoot;
	}

	/**
	 * 清理起点黄色标记。
	 */
	private clearFromPointMarker(): void {
		if (this.fromPointMarkerRoot?.parent) {
			this.fromPointMarkerRoot.parent.remove(this.fromPointMarkerRoot);
		}
		this.fromPointMarkerRoot = null;
		this.viewer.syncSketchEditingVisualLayering({
			previewRoots: [],
		});
	}
}
